import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useParams, Link } from "react-router-dom";
// emoji-mart 5 não traz mais CSS próprio nem exporta React: o componente vem
// do pacote @emoji-mart/react e os emojis do @emoji-mart/data.
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import {
  Paperclip,
  Smile,
  Send,
  X,
  Mic,
  MoreVertical,
  XCircle,
  CheckCircle2,
  Loader2,
  Images,
  ShoppingBag,
  ShoppingCart,
  Clock,
  CalendarClock,
  FileText,
  PlugZap,
  Pencil,
  Crop,
} from "lucide-react";

import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePrefTickets from "../../hooks/usePrefTickets";
import { digitando } from "../../hooks/useAtalhoDeTeclado";
import { IDS_BOTOES_DA_CONVERSA } from "../Ticket/botoesDaConversa";
import { useThemeContext } from "../../context/DarkMode";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import MediaLibraryPicker from "../MediaLibraryPicker";
import EnviarCatalogoModal from "../EnviarCatalogoModal";
import useModules from "../../hooks/useModules";
import useWhatsApps from "../../hooks/useWhatsApps";
import { conexaoFaz } from "../../helpers/capacidadesDoCanal";
import EnviarTemplateModal from "../EnviarTemplateModal";
import ScheduledMessageModal from "../ScheduledMessageModal";
import AssistenteDeTexto from "./AssistenteDeTexto";
import EditorDeImagem from "../EditorDeImagem";
import { ACCEPT_UPLOAD, problemaDoArquivo } from "../../constants/uploads";
import { lerRascunho, gravarRascunho } from "../../services/rascunhoMensagem";
import { rotuloDeMidia, temMiniatura } from "../../helpers/rotuloDeMidia";
import {
  prepararMencoes,
  rotuloDoParticipante,
} from "../../helpers/mencoes";

// Gravação de voz com o MediaRecorder do próprio navegador.
//
// Antes era a mic-recorder-to-mp3, que não funciona mais aqui: ela espera o
// codificador `lamejs` como variável global (formato UMD) e o bundler atual
// carrega tudo como módulo ES. O resultado era `ReferenceError: Lame is not
// defined` na primeira tentativa — o gravador nunca chegava a existir, e o
// botão de áudio simplesmente não fazia nada.
//
// O MediaRecorder é padrão do navegador, não tem dependência e grava em
// Opus — que é justamente o codec que o WhatsApp usa para mensagem de voz.
// O MP3 anterior era convertido de qualquer forma antes de sair.

// Formatos por ordem de preferência; o navegador escolhe o primeiro que
// souber gravar. Safari só tem mp4/aac, Chrome e Firefox preferem opus.
const FORMATOS = [
  { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  { mime: "audio/webm;codecs=opus", ext: "webm" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/webm", ext: "webm" }
];

// Teclado virtual não pode subir sozinho.
//
// No celular, focar o campo ABRE o teclado e come metade da tela — abrir uma
// conversa só para ler viraria uma briga com o teclado a cada toque. Em
// ponteiro fino (mouse/trackpad) focar não custa nada, e é o que faz o cursor
// já estar no lugar certo ao abrir o atendimento.
//
// Vale só para o foco que NASCE do sistema (abrir/trocar conversa, aceitar).
// O que nasce de um gesto que pede digitação — Responder, escolher emoji,
// anexar — foca nos dois, porque ali o teclado é justamente o que se quer.
const ponteiroFino = () =>
  typeof window === "undefined" ||
  typeof window.matchMedia !== "function" ||
  window.matchMedia("(pointer: fine)").matches;

const escolherFormato = () => {
  if (typeof MediaRecorder === "undefined") return null;

  return (
    FORMATOS.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
      mime: "",
      ext: "webm"
    }
  );
};

const MessageInput = ({
  ticketStatus,
  conexaoForaDoAr,
  nomeDaConexao,
  contactId,
  // O agendamento nasce aqui com destinatário e conexão do atendimento: a
  // vontade de "mandar isso amanhã cedo" aparece durante a conversa, não na
  // tela de Agendamentos.
  contact,
  whatsappId,
  onAbrirCarrinho,
  // O botão é um toggle: aceso enquanto o painel está aberto, e o clique
  // seguinte fecha — mesmo gesto dos outros botões de camada.
  carrinhoAberto,
  // "orcamento" | "pagamento" | null — o mesmo sinal do chip da lista; o
  // botão de Vendas só o reflete, sem chamada própria.
  vendaAguardando,
  // Grupo em canal que marca participante: o `@` abre a lista.
  participantes = [],
}) => {
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [agendarAberto, setAgendarAberto] = useState(false);
  const [catalogoAberto, setCatalogoAberto] = useState(false);
  const [loading, setLoading] = useState(false);

  // Guarda a resposta pronta escolhida até a mensagem sair, para então mover
  // o cartão do cliente no quadro. Editar o texto antes de mandar não desfaz
  // a escolha: a resposta continua sendo aquela, com outras palavras.
  const [respostaAplicavel, setRespostaAplicavel] = useState(null);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  // `@` digitado num grupo: onde começa e o que veio depois dele.
  const [mencao, setMencao] = useState(null);
  const [mencaoAtiva, setMencaoAtiva] = useState(0);
  const [previewMedia, setPreviewMedia] = useState("");
  const [editandoImagem, setEditandoImagem] = useState(false);
  const inputRef = useRef();
  // Gravação em curso: o MediaRecorder, o stream do microfone e os pedaços.
  const gravadorRef = useRef(null);
  const pedacosRef = useRef([]);
  const emojiBoxRef = useRef();
  const { setReplyingMessage, replyingMessage, editingMessage, setEditingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const { darkMode } = useThemeContext();

  const disabled = loading || recording || ticketStatus !== "open";

  // A janela de 24 h do WhatsApp oficial. `aplica: false` nos demais canais —
  // ali nunca há prazo e a barra de digitar não pode sumir.
  const [janela, setJanela] = useState({ aplica: false, aberta: true });
  const [templateAberto, setTemplateAberto] = useState(false);

  const conferirJanela = useCallback(async () => {
    if (!ticketId) return;

    try {
      const { data } = await api.get(`/meta/templates/window/${ticketId}`);
      setJanela(data);
    } catch {
      // Sem resposta, o campo de texto continua: bloquear por falha de
      // consulta impediria de responder um canal que nem tem esse limite.
      setJanela({ aplica: false, aberta: true });
    }
  }, [ticketId]);

  useEffect(() => {
    conferirJanela();
  }, [conferirJanela]);

  // Em tela estreita o texto longo do placeholder é cortado no meio.
  const [placeholderCurto, setPlaceholderCurto] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  // Sair da tela no meio de uma gravação deixava o microfone aberto para
  // sempre — a luz do aparelho acesa e o aviso de "gravando" na aba.
  useEffect(() => {
    return () => {
      const atual = gravadorRef.current;
      if (!atual) return;
      try {
        if (atual.recorder.state !== "inactive") atual.recorder.stop();
      } catch {
        // Recorder já encerrado por outro caminho.
      }
      atual.stream.getTracks().forEach((trilha) => trilha.stop());
      gravadorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const aoRedimensionar = () => setPlaceholderCurto(window.innerWidth < 640);
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, []);

  // Assinar é gosto da pessoa, não da máquina: quem assina no computador da
  // mesa quer assinar no notebook também.
  const [signMessage, setSignMessage] = usePrefTickets("assinar", true);
  const [botoesDaConversa] = usePrefTickets(
    "botoesDaConversa",
    IDS_BOTOES_DA_CONVERSA
  );
  // Canal que não envia arquivo (o sidecar de voz do whatsmeow) não mostra
  // clipe, biblioteca nem gravador: o backend recusa com
  // ERR_WHATSMEOW_SEM_MIDIA, e oferecer o botão fazia a pessoa descobrir isso
  // só depois de escolher o arquivo e mandar.
  const { whatsApps } = useWhatsApps();
  const mandaArquivo = conexaoFaz(
    (whatsApps || []).find((w) => w.id === whatsappId),
    "media"
  );
  const BOTOES_DE_ARQUIVO = ["anexar", "biblioteca", "audio"];
  const temBotao = (id) => {
    if (!mandaArquivo && BOTOES_DE_ARQUIVO.includes(id)) return false;

    return botoesDaConversa.includes(id);
  };
  // Catálogo é do módulo "erp" (ModuleKeys.ts): sem contratar, o botão some —
  // a rota /catalog/send responde 403 de qualquer jeito.
  const { temModulo } = useModules();
  const comErp = temModulo("erp");

  // Citação montada (Responder) ou desfeita: o cursor vai para o campo.
  //
  // Só quando o gesto foi DENTRO desta conversa. Trocar de atendimento também
  // zera a citação — é a limpeza do efeito de `[ticketId]` — e chegava aqui
  // com a mesma cara de "cancelou", furando a regra do celular: bastava
  // deixar uma citação aberta no atendimento A e tocar no B para o teclado
  // subir sozinho. Por isso a citação é lembrada JUNTO do atendimento em que
  // nasceu, e a de outra conversa é esquecida em vez de valer como gesto.
  //
  // Ao montar não há gesto nenhum (é a conversa abrindo): vale o ponteiro.
  const citacaoAnterior = useRef({ ticketId, citacao: null });

  useEffect(() => {
    const anterior = citacaoAnterior.current;
    const mesmoAtendimento = anterior.ticketId === ticketId;

    citacaoAnterior.current = {
      ticketId,
      // Fora do atendimento em que nasceu, a citação não conta como gesto —
      // nem agora nem no render seguinte, quando ela finalmente vira null.
      citacao: mesmoAtendimento ? replyingMessage : null
    };

    const houveGesto =
      mesmoAtendimento && Boolean(replyingMessage || anterior.citacao);

    if (houveGesto || ponteiroFino()) inputRef.current?.focus();
  }, [replyingMessage, ticketId]);

  // Rascunho por atendimento. O texto vivia só no estado do componente: trocar
  // de conversa (ou cair a conexão, que troca o campo pela barra de aviso)
  // apagava o que já estava escrito. `localStorage` e não o cookie de
  // preferências porque isto é texto livre e pode ser longo.
  const textoAtual = useRef("");
  textoAtual.current = inputMessage;

  useEffect(() => {
    if (!ticketId) return undefined;

    setInputMessage(lerRascunho(ticketId));

    // Guarda ao sair da conversa; o que foi digitado tecla a tecla já é
    // guardado em `handleChangeInput` (cobre recarregar a página).
    return () => gravarRascunho(ticketId, textoAtual.current);
  }, [ticketId]);

  useEffect(() => {
    // Abrir/trocar de conversa: só em ponteiro fino. No celular, tocar num
    // atendimento é ler, não responder — e o teclado subia sozinho.
    if (ponteiroFino()) inputRef.current?.focus();

    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  // Aceitar/Reabrir: o campo estava desabilitado e o botão que a pessoa
  // clicou some da tela — o foco morria no `body`. O gancho é a transição do
  // STATUS, não o `[ticketId]`: aceitar não muda a URL, então aquele efeito
  // não refire.
  const statusAnterior = useRef(ticketStatus);

  useEffect(() => {
    const anterior = statusAnterior.current;
    statusAnterior.current = ticketStatus;

    if (anterior === "open" || ticketStatus !== "open") return;
    if (!ponteiroFino()) return;

    // A mesma guarda dos atalhos de teclado (`useAtalhoDeTeclado`), e não uma
    // cópia: quem está digitando não é interrompido, e um diálogo aberto é
    // dono do foco enquanto estiver ali.
    //
    // O status pode virar "open" sem que esta pessoa tenha feito nada — outro
    // atendente aceita o ticket e o socket avisa. Sem a conferência do
    // diálogo, o foco pulava para o campo ATRÁS da galeria aberta, e o botão
    // que a pessoa ia clicar deixava de responder ao Enter.
    if (digitando(document.activeElement)) return;
    if (document.querySelector("[role=dialog]")) return;

    inputRef.current?.focus();
  }, [ticketStatus]);

  useEffect(() => {
    if (!showEmoji) return undefined;

    const handleClickAway = (e) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };

    // Antes só o clique fora fechava: com o teclado, a pessoa abria o painel
    // e ficava presa nele. Esc fecha e o cursor volta para o campo.
    const aoTeclar = (e) => {
      if (e.key !== "Escape") return;

      setShowEmoji(false);
      inputRef.current?.focus();
    };

    document.addEventListener("mousedown", handleClickAway);
    document.addEventListener("keydown", aoTeclar);

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [showEmoji]);

  // Anexo entrou ou saiu.
  //
  // A barra de anexo troca o campo de escrever pela textarea da legenda: sem
  // isto, escolher um arquivo deixava o foco no botão do clipe (ou no `body`,
  // quando o diálogo do sistema fecha) e cancelar o anexo deixava o foco em
  // lugar nenhum — a pessoa tinha de clicar no campo para voltar a escrever.
  const legendaRef = useRef();
  const anexoAnterior = useRef({ ticketId, tinha: false });

  useEffect(() => {
    const anterior = anexoAnterior.current;
    const mesmoAtendimento = anterior.ticketId === ticketId;
    const tem = medias.length > 0;

    // Mesma regra da citação: o anexo pertence ao atendimento em que foi
    // escolhido. Trocar de conversa também esvazia `medias` (limpeza do
    // efeito de `[ticketId]`), e sem isto a troca era lida como "cancelou o
    // anexo" — no celular o teclado subia só por abrir outra conversa.
    anexoAnterior.current = { ticketId, tinha: mesmoAtendimento && tem };

    if (!mesmoAtendimento) return;

    // Legenda só em ponteiro fino: no celular o teclado taparia a prévia do
    // arquivo que a pessoa acabou de escolher e quer conferir.
    if (!anterior.tinha && tem) {
      if (ponteiroFino()) legendaRef.current?.focus();
    } else if (anterior.tinha && !tem) {
      // Cancelou (ou o anexo foi enviado): de volta ao campo, nos dois.
      inputRef.current?.focus();
    }
  }, [medias, ticketId]);

  // Miniatura da mídia anexada, só para imagens.
  useEffect(() => {
    const arquivo = medias[0];
    if (!arquivo || !arquivo.type?.startsWith("image/")) {
      setPreviewMedia("");
      return undefined;
    }

    const url = window.URL.createObjectURL(arquivo);
    setPreviewMedia(url);

    return () => window.URL.revokeObjectURL(url);
  }, [medias]);

  // Fim da gravação (enviou ou desistiu): o campo fica `disabled` enquanto o
  // microfone corre, então o foco só pode voltar depois que ele volta a
  // aceitar — `recording` E `loading` em false, senão o `focus()` cai num
  // campo desabilitado e não faz nada.
  const focarAposGravar = useRef(false);

  useEffect(() => {
    if (recording) {
      focarAposGravar.current = true;
      return;
    }

    if (focarAposGravar.current && !loading) {
      focarAposGravar.current = false;
      inputRef.current?.focus();
    }
  }, [recording, loading]);

  // O copiloto (no cabeçalho do ticket) manda o texto por evento, para não
  // precisar levantar o estado da caixa de mensagem até lá.
  useEffect(() => {
    const aoReceber = (evento) => {
      setInputMessage(evento.detail || "");
      inputRef.current?.focus();
    };

    document.addEventListener("copilot:usar-texto", aoReceber);
    return () => document.removeEventListener("copilot:usar-texto", aoReceber);
  }, []);

  // Ponte de foco para quem está fora daqui.
  //
  // Reagir, encaminhar e sair da busca acontecem na lista de mensagens, e o
  // gatilho que a pessoa clicou desmonta junto com o menu — o foco morre no
  // `body`. Em vez de levantar o estado do campo até lá, quem terminou a ação
  // dispara `document.dispatchEvent(new CustomEvent("composer:focar"))` e o
  // cursor volta para cá. Mesmo padrão do `copilot:usar-texto` acima.
  useEffect(() => {
    const aoPedirFoco = () => inputRef.current?.focus();

    document.addEventListener("composer:focar", aoPedirFoco);
    return () => document.removeEventListener("composer:focar", aoPedirFoco);
  }, []);

  const handleChangeInput = (e) => {
    setInputMessage(e.target.value);
    gravarRascunho(ticketId, e.target.value);
    handleLoadQuickAnswer(e.target.value);
    procurarMencao(e.target.value, e.target.selectionStart);

    // Apagou tudo: desistiu daquela resposta. Sem isto, escrever outra coisa
    // do zero ainda moveria o card da resposta escolhida antes.
    if (!e.target.value) setRespostaAplicavel(null);
  };

  const aplicarMovimentoDaResposta = async () => {
    if (!respostaAplicavel) return;

    const { id } = respostaAplicavel;
    setRespostaAplicavel(null);

    try {
      const { data } = await api.post(`/quickAnswers/${id}/aplicar`, {
        ticketId,
      });

      // Mover em silêncio deixa a pessoa sem saber se funcionou — e não
      // mover em silêncio é pior ainda: parece defeito. Cada caso fala.
      if (data?.cartao) {
        toast.success(
          data.cartao.criado
            ? `Cartão aberto em "${data.cartao.coluna}".`
            : `Cartão movido para "${data.cartao.coluna}".`
        );
      } else if (data?.semEfeito === "ja-na-coluna") {
        toast.info(`O cartão já estava em "${data.coluna}".`);
      } else if (data?.semEfeito === "sem-vinculo") {
        toast.info(
          "Esta resposta pronta não está ligada a nenhuma coluna do Kanban."
        );
      }
    } catch (err) {
      // A mensagem já chegou ao cliente; o cartão é que não andou.
      toast.warn("A mensagem foi enviada, mas não consegui mover o cartão.");
    }
  };

  const procurarMencao = (texto, cursor) => {
    const achado = participantes.length
      ? /(^|\s)@([^\s@]{0,30})$/.exec(texto.slice(0, cursor))
      : null;

    setMencao(
      achado ? { inicio: cursor - achado[2].length - 1, busca: achado[2] } : null,
    );
    setMencaoAtiva(0);
  };

  const sugestoesDeMencao = mencao
    ? participantes
        .filter((p) =>
          `${rotuloDoParticipante(p)} ${p.numero || ""}`
            .toLowerCase()
            .includes(mencao.busca.toLowerCase()),
        )
        .slice(0, 8)
    : [];

  const escolherMencao = (p) => {
    const campo = inputRef.current;
    const fim = campo?.selectionStart ?? inputMessage.length;
    const marca = `@${rotuloDoParticipante(p)} `;
    const novo =
      inputMessage.slice(0, mencao.inicio) + marca + inputMessage.slice(fim);

    setInputMessage(novo);
    gravarRascunho(ticketId, novo);
    setMencao(null);

    requestAnimationFrame(() => {
      campo?.focus();
      campo?.setSelectionRange(
        mencao.inicio + marca.length,
        mencao.inicio + marca.length,
      );
    });
  };

  const handleQuickAnswersClick = async (resposta) => {
    setTypeBar(false);
    setInputMessage(resposta.message || "");

    setRespostaAplicavel(resposta.moveKanbanColumnId ? resposta : null);

    if (!resposta.mediaUrl) return;

    // Traz o arquivo salvo e o põe como anexo pendente, igual a quem escolhe
    // um arquivo do computador. Nada sai sozinho: a pessoa ainda revisa e
    // clica em enviar, que é a promessa da tela de respostas prontas.
    try {
      setLoading(true);
      const resposta_http = await fetch(resposta.mediaUrl);

      if (!resposta_http.ok) throw new Error("Arquivo não encontrado");

      const blob = await resposta_http.blob();
      const nome =
        resposta.mediaName || resposta.mediaUrl.split("/").pop() || "arquivo";

      setMedias([new File([blob], nome, { type: blob.type })]);
    } catch (err) {
      toast.error(
        "Não consegui carregar o arquivo desta resposta. O texto foi preenchido."
      );
    }
    setLoading(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);

    // O painel continua aberto (dá para escolher vários), mas o cursor volta
    // para o campo: sem isto, escolher um emoji e continuar digitando exigia
    // clicar no campo de novo.
    inputRef.current?.focus();
  };

  // Filtra o que o backend recusaria, dizendo o motivo por arquivo. Sem isto
  // o upload inteiro subia para só então voltar com um erro genérico.
  const aceitarArquivos = (arquivos) => {
    const bons = [];

    arquivos.forEach((arquivo) => {
      const problema = problemaDoArquivo(arquivo);

      if (problema) toast.error(`${arquivo.name}: ${problema}.`);
      else bons.push(arquivo);
    });

    return bons;
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }

    const escolhidos = aceitarArquivos(Array.from(e.target.files));

    if (escolhidos.length) setMedias(escolhidos);

    // Zera o input: escolher o mesmo arquivo de novo, depois de uma recusa,
    // não dispara `change` se o valor continuar lá.
    e.target.value = "";
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      const [colado] = aceitarArquivos([e.clipboardData.files[0]]);

      if (colado) setMedias([colado]);
    }
  };

  // Upload em curso, por atendimento. O composer volta a aceitar texto e outro
  // anexo na hora — travar tudo até um vídeo subir era o defeito — e a linha
  // "Enviando…" diz que o arquivo ainda está a caminho.
  const [uploads, setUploads] = useState([]);
  const ticketAtualRef = useRef(ticketId);
  useEffect(() => {
    ticketAtualRef.current = ticketId;
  }, [ticketId]);
  const uploadsDoTicket = uploads.filter((u) => u.ticketId === ticketId);

  const subirEmSegundoPlano = async (formData, rotulo, aoFalhar) => {
    const id = `${Date.now()}-${Math.random()}`;
    const doTicket = ticketId;

    setUploads((atual) => [...atual, { id, ticketId: doTicket, rotulo }]);

    try {
      await api.post(`/messages/${doTicket}`, formData);
      return true;
    } catch (err) {
      toastError(err);
      // Devolve o anexo só se a pessoa continua na mesma conversa: restaurar
      // em outra mandaria o arquivo para o cliente errado no próximo Enter.
      if (ticketAtualRef.current === doTicket) aoFalhar();
      return false;
    } finally {
      setUploads((atual) => atual.filter((u) => u.id !== id));
    }
  };

  const handleUploadMedia = async (e) => {
    e.preventDefault();

    const legenda = inputMessage.trim();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => formData.append("medias", media));
    // `body` é a legenda opcional — vai uma só, para o conjunto de arquivos.
    if (legenda) {
      formData.append(
        "body",
        signMessage ? `*${user?.name}:*\n${legenda}` : legenda
      );
    }
    // Multipart só carrega string: a citação vai serializada e o controller
    // faz o parse. Sem isto, responder com foto perdia a citação.
    if (replyingMessage) {
      formData.append("quotedMsg", JSON.stringify(replyingMessage));
    }

    // Mesmo gesto do texto: esvazia na hora, o POST corre solto e, se
    // falhar, o anexo e a legenda voltam — sem atropelar um anexo novo.
    const arquivos = medias;
    const citacao = replyingMessage;

    setMedias([]);
    setInputMessage("");
    setReplyingMessage(null);
    gravarRascunho(ticketId, "");

    const rotulo =
      arquivos.length > 1 ? `${arquivos.length} arquivos` : arquivos[0]?.name;

    const ok = await subirEmSegundoPlano(formData, rotulo, () => {
      setMedias((atual) => (atual.length ? atual : arquivos));
      setInputMessage((atual) => atual || legenda);
      setReplyingMessage((atual) => atual || citacao);
    });

    if (ok) aplicarMovimentoDaResposta();
  };

  // Entrar em edição traz o texto atual para o campo — é o que a pessoa vai
  // corrigir. Guarda-se o que estava escrito para devolver ao cancelar: perder
  // um rascunho de três linhas porque alguém clicou em "Editar" por engano é
  // dano pequeno e evitável.
  const [rascunhoAntesDaEdicao, setRascunhoAntesDaEdicao] = useState("");
  const [idEmEdicao, setIdEmEdicao] = useState(null);

  // Ajuste de estado durante a renderização, não em efeito: é o padrão do
  // React para "props mudaram, derive o estado" e é o que a regra
  // `set-state-in-effect` pede. Guardado por `id` — a mensagem é reemitida
  // pelo socket a cada ack, e reagir ao objeto reescreveria o campo enquanto
  // a pessoa digita a correção.
  if (editingMessage && editingMessage.id !== idEmEdicao) {
    setIdEmEdicao(editingMessage.id);
    setRascunhoAntesDaEdicao(inputMessage);
    setInputMessage(editingMessage.body || "");
  }

  if (!editingMessage && idEmEdicao) setIdEmEdicao(null);

  const cancelarEdicao = () => {
    setEditingMessage(null);
    setInputMessage(rascunhoAntesDaEdicao);
    setRascunhoAntesDaEdicao("");
  };

  const handleSalvarEdicao = async () => {
    const texto = inputMessage.trim();

    if (!texto) return;

    setLoading(true);

    try {
      await api.put(`/messages/${editingMessage.id}`, { body: texto });
      setEditingMessage(null);
      setInputMessage(rascunhoAntesDaEdicao);
      setRascunhoAntesDaEdicao("");
    } catch (err) {
      // O texto FICA no campo: a edição falhou (janela expirada, canal sem o
      // recurso) e reescrever tudo de novo seria castigo por um erro do
      // provider.
      toastError(err);
    }

    setLoading(false);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;

    // No celular o teclado fechava a cada envio: o botão rouba o foco do
    // textarea, e durante o `await` do POST não há campo focado — o navegador
    // recolhe o teclado e a pessoa precisa tocar de novo para escrever a
    // próxima frase. Devolver o foco ANTES da requisição mantém o teclado
    // aberto, que é como o WhatsApp se comporta.
    inputRef.current?.focus();

    const texto = inputMessage.trim();
    const { texto: comMencoes, mencoes } = prepararMencoes(
      texto,
      participantes,
    );
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage ? `*${user?.name}:*\n${comMencoes}` : comMencoes,
      quotedMsg: replyingMessage,
      ...(mencoes.length ? { mencoes } : {}),
    };

    // O campo esvazia na hora e o POST corre solto: travar o textarea até a
    // resposta do servidor impede de digitar a frase seguinte, que é o que
    // todo mensageiro deixa fazer. Se falhar, o texto volta para o campo.
    const doTicket = ticketId;
    setInputMessage("");
    gravarRascunho(ticketId, "");
    setMencao(null);
    setShowEmoji(false);
    setReplyingMessage(null);

    try {
      await api.post(`/messages/${doTicket}`, message);
      aplicarMovimentoDaResposta();
    } catch (err) {
      toastError(err);

      // A pessoa já está em outra conversa: devolver o texto no campo dela o
      // mandaria ao cliente errado no próximo Enter. Ele volta como rascunho
      // do atendimento de origem, que reaparece ao reabri-lo.
      if (ticketAtualRef.current !== doTicket) {
        const anterior = lerRascunho(doTicket);
        gravarRascunho(doTicket, anterior ? `${texto}\n${anterior}` : texto);
        toast.info("A mensagem não saiu e voltou como rascunho na conversa de origem.");
        return;
      }

      // Falhou: devolve o texto SEM apagar o que já foi digitado enquanto o
      // POST corria, e devolve a citação se nenhuma outra tomou o lugar.
      setInputMessage((atual) => {
        const restaurado = atual ? `${texto}\n${atual}` : texto;
        gravarRascunho(ticketId, restaurado);
        return restaurado;
      });
      setReplyingMessage((atual) => atual || message.quotedMsg);
    }
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          // `value` é o texto recém-digitado; `inputMessage` ainda tem o valor
          // anterior neste ponto do render e faria a busca ficar um passo atrás.
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers.length > 0);
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      // Sem HTTPS o navegador nem expõe o microfone. Acontece direto em
      // produção servida por HTTP, e o erro que aparecia era um
      // "undefined is not a function" que não dizia nada.
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error(
          "O navegador só libera o microfone em conexão segura (https). " +
            "Peça para quem cuida do servidor habilitar o certificado."
        );
        setLoading(false);
        return;
      }

      const formato = escolherFormato();

      if (!formato) {
        toast.error("Este navegador não sabe gravar áudio.");
        setLoading(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // 32 kbps é a taxa que o WhatsApp usa na voz. O padrão do navegador é
      // várias vezes isso, e o arquivo maior só atrasaria o upload.
      const recorder = new MediaRecorder(stream, {
        ...(formato.mime ? { mimeType: formato.mime } : {}),
        audioBitsPerSecond: 32000,
      });

      pedacosRef.current = [];
      // Guarda o stream junto: parar o recorder não desliga o microfone, e
      // sem fechar as trilhas a luz do aparelho fica acesa depois da gravação.
      gravadorRef.current = { recorder, stream, ext: formato.ext };

      recorder.ondataavailable = (evento) => {
        if (evento.data && evento.data.size > 0) {
          pedacosRef.current.push(evento.data);
        }
      };

      // Um pedaço por segundo: sem o intervalo, o navegador só entrega tudo
      // no fim, e uma aba fechada no meio levaria a gravação inteira.
      recorder.start(1000);

      setRecording(true);
      setLoading(false);
    } catch (err) {
      // Permissão negada é o caso mais comum e não é defeito do sistema:
      // merece instrução, não a mensagem técnica do navegador.
      if (err?.name === "NotAllowedError") {
        toast.error(
          "Você bloqueou o microfone para este site. Libere no cadeado ao " +
            "lado do endereço e tente de novo."
        );
      } else if (err?.name === "NotFoundError") {
        toast.error("Nenhum microfone encontrado neste computador.");
      } else {
        toastError(err);
      }
      setLoading(false);
    }
  };

  // Encerra a gravação e devolve o áudio. Fecha o microfone em qualquer
  // caso — inclusive quando a pessoa desiste.
  const encerrarGravacao = () =>
    new Promise((resolve) => {
      const atual = gravadorRef.current;

      if (!atual) {
        resolve(null);
        return;
      }

      const finalizar = () => {
        atual.stream.getTracks().forEach((trilha) => trilha.stop());
        gravadorRef.current = null;

        const blob = new Blob(pedacosRef.current, {
          type: atual.recorder.mimeType || "audio/webm"
        });
        pedacosRef.current = [];

        resolve({ blob, ext: atual.ext });
      };

      if (atual.recorder.state === "inactive") {
        finalizar();
        return;
      }

      atual.recorder.onstop = finalizar;
      atual.recorder.stop();
    });

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const gravado = await encerrarGravacao();

      if (!gravado) {
        setRecording(false);
        setLoading(false);
        return;
      }

      // Áudio curto demais era descartado em silêncio: a pessoa gravava,
      // soltava, e simplesmente não acontecia nada — parecia microfone
      // quebrado. Agora ela sabe por que não foi.
      if (gravado.blob.size < 2000) {
        toast.info("Gravação muito curta. Segure o botão enquanto fala.");
        setLoading(false);
        setRecording(false);
        return;
      }

      // Sobe o que o navegador gravou, sem converter: o backend transforma em
      // OGG/Opus (`arquivoDeVoz`), que é o formato da mensagem de voz. Antes
      // a tela convertia para WAV, que é 10 vezes maior, e o upload demorava.
      const filename = `${new Date().getTime()}.${gravado.ext}`;
      const arquivo = new File([gravado.blob], filename, {
        type: gravado.blob.type,
      });

      const formData = new FormData();
      formData.append("medias", arquivo);
      // Sem legenda: mandar o nome do arquivo aqui fazia o áudio chegar no
      // celular do cliente com "1785342981.mp3" escrito embaixo, e a lista de
      // atendimentos mostrar isso como prévia da conversa.
      formData.append("body", "");
      formData.append("fromMe", true);

      // O campo volta já; o áudio sobe em segundo plano. Falhou, ele vira
      // anexo pendente — regravar a fala inteira seria o castigo.
      setRecording(false);
      setLoading(false);

      await subirEmSegundoPlano(formData, "Áudio", () =>
        setMedias((atual) => (atual.length ? atual : [arquivo]))
      );
      return;
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    await encerrarGravacao();
    setRecording(false);
  };

  const renderReplyingMessage = (message) => {
    return (
      <div className="flex w-full items-center gap-2 px-3 pt-2">
        <div
          className={cn(
            "flex flex-1 overflow-hidden rounded-lg",
            !message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-black/5 dark:bg-white/10"
          )}
        >
          <span
            className={cn("w-1 shrink-0", !message.fromMe ? "bg-emerald-400" : "bg-sky-400")}
          />
          <div className="min-w-0 flex-1 whitespace-pre-wrap p-2.5 text-sm">
            {!message.fromMe && (
              <span className="flex font-medium text-sky-500">
                {message.contact?.name}
              </span>
            )}
            <span className="line-clamp-2">
              {message.body || rotuloDeMidia(message.mediaType)}
            </span>
          </div>
          {temMiniatura(message) &&
            (message.mediaType === "video" ? (
              <video
                className="h-12 w-12 shrink-0 object-cover"
                src={message.mediaUrl}
                preload="metadata"
                muted
              />
            ) : (
              <img
                className="h-12 w-12 shrink-0 object-cover"
                src={message.mediaUrl}
                alt=""
              />
            ))}
        </div>
        <Button
          variant="ghost"
          size="icon"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const signToggle = (
    <div className="flex items-center gap-1.5">
      <Switch
        id="signMessage"
        checked={signMessage}
        onCheckedChange={(checked) => setSignMessage(checked)}
      />
      <Label htmlFor="signMessage" className="text-xs font-normal text-muted-foreground">
        {i18n.t("messagesInput.signMessage")}
      </Label>
    </div>
  );

  if (medias.length > 0) {
    return (
      <div className="flex flex-none items-center gap-2 border-t bg-muted/40 p-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setMedias([]);
            setInputMessage("");
          }}
          disabled={loading}
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </Button>

        {previewMedia && (
          <img
            src={previewMedia}
            alt=""
            className="h-10 w-10 shrink-0 rounded object-cover"
          />
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-muted-foreground">
            {medias.length > 1
              ? `${medias.length} arquivos`
              : medias[0]?.name}
          </p>
          <textarea
            ref={legendaRef}
            rows={1}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={loading}
            placeholder="Escreva uma legenda (opcional)"
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleUploadMedia(e);
              }
            }}
            className="max-h-24 w-full resize-none rounded-lg border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
          />
        </div>

        {previewMedia && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Editar imagem"
            title="Editar imagem"
            onClick={() => setEditandoImagem(true)}
            disabled={loading}
          >
            <Crop className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          aria-label="Enviar arquivo"
          title="Enviar arquivo"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
          ) : (
            <Send className="h-5 w-5 text-muted-foreground" />
          )}
        </Button>

        <EditorDeImagem
          aberto={editandoImagem}
          arquivo={medias[0]}
          onFechar={() => setEditandoImagem(false)}
          onConcluir={(editada) => {
            // Só a primeira entra no editor; as outras do lote continuam lá.
            setMedias((anterior) => [editada, ...anterior.slice(1)]);
            setEditandoImagem(false);
          }}
        />
      </div>
    );
  }

  // Conexão do atendimento fora do ar: nada que for digitado aqui chega ao
  // cliente. A barra some pelo mesmo motivo da janela de 24 h — deixar o campo
  // aberto convida a escrever no vazio e só avisa depois do enter, quando o
  // atendente já acha que respondeu.
  if (conexaoForaDoAr && ticketStatus === "open") {
    return (
      <div className="flex flex-none flex-col gap-2 border-t bg-destructive/10 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-start gap-2 text-sm">
          <PlugZap className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p>
            A conexão{" "}
            {nomeDaConexao ? (
              <strong>{nomeDaConexao}</strong>
            ) : (
              "deste atendimento"
            )}{" "}
            está fora do ar. Nada enviado agora chega ao cliente — reconecte em
            Conexões e volte aqui.
          </p>
        </div>

        <Button asChild variant="outline" className="self-start">
          <Link to="/connections">
            <PlugZap className="h-4 w-4" />
            Abrir Conexões
          </Link>
        </Button>
      </div>
    );
  }

  // Janela de 24 h fechada: texto livre não sai, e insistir gasta o erro
  // 131047 da Meta depois de a mensagem já aparecer como enviada aqui. O
  // caminho passa a ser o modelo aprovado, e a barra some para deixar isso
  // claro em vez de deixar alguém digitar no vazio.
  if (janela.aplica && !janela.aberta && ticketStatus === "open") {
    return (
      <div className="flex flex-none flex-col gap-2 border-t bg-amber-500/10 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-start gap-2 text-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p>
            Passaram-se mais de 24 h desde a última mensagem deste cliente. O
            WhatsApp só aceita modelo aprovado agora — enviar um reabre a
            conversa por mais 24 h.
          </p>
        </div>

        <Button
          className="self-start"
          onClick={() => setTemplateAberto(true)}
          disabled={loading}
        >
          <FileText className="h-4 w-4" />
          Enviar modelo
        </Button>

        <EnviarTemplateModal
          open={templateAberto}
          ticketId={ticketId}
          onClose={() => setTemplateAberto(false)}
          onEnviado={conferirJanela}
        />
      </div>
    );
  }

  return (
    <div className="relative flex flex-none flex-col border-t bg-muted/40 pb-[env(safe-area-inset-bottom)]">
      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        ticketId={ticketId}
      />

      <EnviarCatalogoModal
        open={catalogoAberto}
        onClose={() => setCatalogoAberto(false)}
        ticketId={ticketId}
        contactId={contactId}
        onAdicionadoAoCarrinho={() => onAbrirCarrinho?.()}
      />

      <EnviarTemplateModal
        open={templateAberto}
        ticketId={ticketId}
        onClose={() => setTemplateAberto(false)}
        onEnviado={conferirJanela}
      />

      <ScheduledMessageModal
        open={agendarAberto}
        onClose={() => setAgendarAberto(false)}
        contatoInicial={contact}
        whatsappIdInicial={whatsappId}
        textoInicial={inputMessage}
        onSaved={() => {
          // O texto virou agendamento: deixá-lo no campo convidaria a mandar
          // a mesma frase duas vezes, agora e na hora marcada. A citação sai
          // junto — o agendamento não a leva, e deixá-la armada faria a
          // próxima mensagem sair como resposta a uma conversa já passada.
          setInputMessage("");
          gravarRascunho(ticketId, "");
          setReplyingMessage(null);
        }}
      />

      {/* Fora das barras desktop/mobile: assim o seletor funciona nos dois. */}
      {showEmoji && (
        <div
          ref={emojiBoxRef}
          className="absolute bottom-full left-2 right-2 z-20 mb-2 flex justify-center sm:right-auto sm:justify-start"
        >
          <div className="max-w-full overflow-x-auto rounded-lg shadow-lg">
            <Picker
              data={emojiData}
              locale="pt"
              perLine={8}
              previewPosition="none"
              skinTonePosition="none"
              theme={darkMode ? "dark" : "light"}
              onEmojiSelect={handleAddEmoji}
            />
          </div>
        </div>
      )}
      {editingMessage && (
        <div className="flex items-center gap-2 border-t bg-muted/60 px-3 py-2 text-sm">
          <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">
            Editando a mensagem enviada
            <span className="ml-2 text-muted-foreground">
              O texto muda também no aparelho de quem recebeu.
            </span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            disabled={loading}
            aria-label="Cancelar edição"
            onClick={cancelarEdicao}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {replyingMessage && renderReplyingMessage(replyingMessage)}
      {uploadsDoTicket.length > 0 && (
        <div
          role="status"
          className="flex items-center gap-2 border-t bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
        >
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-emerald-500" />
          <span className="min-w-0 truncate">
            Enviando {uploadsDoTicket.map((u) => u.rotulo).join(", ")}…
          </span>
        </div>
      )}
      <div className="flex w-full items-center gap-1 p-2">
        <div className="hidden items-center gap-0.5 sm:flex">
          {temBotao("emoji") && (
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label="Emoji"
              onClick={() => setShowEmoji((prev) => !prev)}
            >
              <Smile className="h-5 w-5 text-muted-foreground" />
            </Button>
          )}

          <input
            multiple
            type="file"
            id="upload-button"
            className="hidden"
            disabled={disabled}
            // O `accept` filtra o que aparece no seletor do sistema; a
            // validação acima é o que realmente barra, porque o accept é
            // uma sugestão e o usuário pode ignorá-la.
            accept={ACCEPT_UPLOAD}
            onChange={handleChangeMedias}
          />
          {temBotao("anexar") && (
            <label htmlFor="upload-button">
              <Button variant="ghost" size="icon" asChild disabled={disabled}>
                <span aria-label="Anexar arquivo">
                  <Paperclip className="h-5 w-5 text-muted-foreground" />
                </span>
              </Button>
            </label>
          )}

          {temBotao("biblioteca") && (
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label="Enviar da biblioteca"
              onClick={() => setLibraryOpen(true)}
            >
              <Images className="h-5 w-5 text-muted-foreground" />
            </Button>
          )}

          {temBotao("agendar") && (
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled || !contact?.id}
              aria-label="Agendar envio"
              title="Agendar envio"
              onClick={() => setAgendarAberto(true)}
            >
              <CalendarClock className="h-5 w-5 text-muted-foreground" />
            </Button>
          )}

          {temBotao("catalogo") && comErp && (
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label="Enviar catálogo"
              onClick={() => setCatalogoAberto(true)}
            >
              <ShoppingBag className="h-5 w-5 text-muted-foreground" />
            </Button>
          )}

          {temBotao("carrinho") && comErp && (
            <Button
              variant="ghost"
              size="icon"
              className={`relative ${carrinhoAberto ? "bg-accent" : ""}`}
              aria-pressed={!!carrinhoAberto}
              aria-label={
                vendaAguardando
                  ? `Vendas — ${
                      vendaAguardando === "pagamento"
                        ? "pedido a pagar"
                        : "orçamento aguardando resposta"
                    }`
                  : "Vendas"
              }
              title={
                vendaAguardando === "pagamento"
                  ? "Pedido esperando pagamento"
                  : vendaAguardando
                    ? "Orçamento esperando resposta"
                    : undefined
              }
              onClick={() => onAbrirCarrinho?.()}
            >
              <ShoppingCart
                className={`h-5 w-5 ${
                  carrinhoAberto ? "text-primary" : "text-muted-foreground"
                }`}
              />
              {vendaAguardando && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-background" />
              )}
            </Button>
          )}

          {temBotao("assinar") && (
            <div className="ml-1 mr-2">{signToggle}</div>
          )}
        </div>

        <div className="flex sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Mais opções">
                <MoreVertical className="h-5 w-5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowEmoji((prev) => !prev);
                }}
                disabled={disabled}
              >
                <Smile className="h-4 w-4" /> Emoji
              </DropdownMenuItem>
              {/* Mesma regra dos botões da barra: canal que não envia arquivo
                  não oferece anexo nem biblioteca nem aqui dentro. */}
              {mandaArquivo && (
                <>
                  <DropdownMenuItem asChild>
                    <label htmlFor="upload-button" className="flex cursor-pointer items-center gap-2">
                      <Paperclip className="h-4 w-4" /> Anexar
                    </label>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setLibraryOpen(true);
                    }}
                    disabled={disabled}
                  >
                    <Images className="h-4 w-4" /> Biblioteca
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setAgendarAberto(true);
                }}
                disabled={disabled || !contact?.id}
              >
                <CalendarClock className="h-4 w-4" /> Agendar envio
              </DropdownMenuItem>
              {comErp && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setCatalogoAberto(true);
                  }}
                  disabled={disabled}
                >
                  <ShoppingBag className="h-4 w-4" /> Catálogo
                </DropdownMenuItem>
              )}
              {comErp && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    onAbrirCarrinho?.();
                  }}
                >
                  <ShoppingCart className="h-4 w-4" /> Vendas
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                {signToggle}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="relative flex-1">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={
              ticketStatus !== "open"
                ? i18n.t("messagesInput.placeholderClosed")
                : placeholderCurto
                ? "Mensagem"
                : i18n.t("messagesInput.placeholderOpen")
            }
            value={inputMessage}
            onChange={handleChangeInput}
            disabled={recording || loading || ticketStatus !== "open"}
            onPaste={(e) => {
              ticketStatus === "open" && handleInputPaste(e);
            }}
            onKeyPress={(e) => {
              if (loading || e.shiftKey) return;
              else if (e.key === "Enter") {
                e.preventDefault();
                if (editingMessage) {
                  handleSalvarEdicao();
                } else {
                  handleSendMessage();
                }
              }
            }}
            // Esc devolve o teclado à navegação. Abrir uma conversa põe o
            // cursor aqui — e, enquanto ele estiver aqui, j e k são letras,
            // não atalhos. Sem uma saída, andar na lista pelo teclado só
            // funcionaria antes da primeira conversa aberta.
            //
            // Em degraus, do mais perto para o mais longe: com o painel de
            // emoji aberto quem fecha é ele (o ouvinte do documento); com uma
            // citação montada, o primeiro Esc desfaz a citação e o cursor FICA
            // aqui — desfazer a citação e perder o campo junto obrigaria a
            // clicar de novo para continuar a mesma resposta. Só o Esc
            // seguinte sai do campo.
            onKeyDown={(e) => {
              // Lista do `@` aberta: setas andam, Enter/Tab escolhem, Esc
              // fecha — antes do Enter de enviar.
              if (sugestoesDeMencao.length) {
                const total = sugestoesDeMencao.length;

                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  setMencaoAtiva(
                    (i) => (i + (e.key === "ArrowDown" ? 1 : total - 1)) % total,
                  );
                  return;
                }

                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  escolherMencao(sugestoesDeMencao[mencaoAtiva] || sugestoesDeMencao[0]);
                  return;
                }

                if (e.key === "Escape") {
                  e.preventDefault();
                  setMencao(null);
                  return;
                }
              }

              if (e.key !== "Escape") return;

              if (showEmoji) return;

              if (editingMessage) {
                e.preventDefault();
                cancelarEdicao();
                return;
              }

              if (replyingMessage) {
                e.preventDefault();
                setReplyingMessage(null);
                return;
              }

              e.currentTarget.blur();
            }}
            className="max-h-32 w-full resize-none overflow-y-auto rounded-2xl border bg-background px-4 py-2.5 text-sm shadow-xs scrollbar-none placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 [&::-webkit-scrollbar]:hidden"
          />
          {sugestoesDeMencao.length > 0 && (
            <ul
              role="listbox"
              aria-label="Marcar participante"
              className="absolute bottom-full left-0 z-20 mb-1 max-h-64 w-full max-w-sm overflow-y-auto rounded-lg border bg-popover shadow-md"
            >
              {sugestoesDeMencao.map((p, i) => (
                <li key={p.id} className="list-none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === mencaoAtiva}
                    // `mousedown` tira o foco do campo antes do clique; sem
                    // isto o cursor se perde e a marca entra no lugar errado.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => escolherMencao(p)}
                    className={cn(
                      "flex w-full flex-col px-3 py-1.5 text-left text-sm hover:bg-accent",
                      i === mencaoAtiva && "bg-accent",
                    )}
                  >
                    <span className="truncate font-medium">
                      {rotuloDoParticipante(p)}
                    </span>
                    {p.nome && p.numero && (
                      <span className="truncate text-xs text-muted-foreground">
                        +{p.numero}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {typeBar && (
            <ul className="absolute bottom-full left-0 mb-1 w-full overflow-hidden rounded-lg border bg-popover shadow-md">
              {quickAnswers.map((value, index) => (
                <li key={index} className="list-none">
                  <button
                    type="button"
                    onClick={() => handleQuickAnswersClick(value)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    {/* Clipe avisa que vem arquivo junto: sem isso a pessoa
                        escolhe achando que é só texto e leva um anexo. */}
                    {value.mediaUrl && (
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">
                      {value.message
                        ? `${value.shortcut} - ${value.message}`
                        : `${value.shortcut} - ${value.mediaName || "arquivo"}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Correção e sugestão ficam colados no campo, e não num painel à
            parte: é aqui que a pessoa está escrevendo quando precisa delas. */}
        {temBotao("iaCorrigir") && (
        <AssistenteDeTexto
          ticketId={ticketId}
          texto={inputMessage}
          aoTrocarTexto={setInputMessage}
          desabilitado={recording || loading || ticketStatus !== "open"}
        />
        )}

        {inputMessage ? (
          <Button
            size="icon"
            className="shrink-0 rounded-full"
            aria-label="Enviar mensagem"
            // Impede o botão de tirar o foco do textarea. Sem isto o blur
            // acontece antes do clique e o teclado do celular já começa a
            // fechar — o focus() do handler chegaria tarde.
            onPointerDown={(e) => e.preventDefault()}
            onClick={editingMessage ? handleSalvarEdicao : handleSendMessage}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        ) : recording ? (
          <div className="flex items-center">
            <Button variant="ghost" size="icon" disabled={loading} onClick={handleCancelAudio}>
              <XCircle className="h-5 w-5 text-destructive" />
            </Button>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            ) : (
              <RecordingTimer />
            )}
            <Button variant="ghost" size="icon" onClick={handleUploadAudio} disabled={loading}>
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            </Button>
          </div>
        ) : (
          temBotao("audio") && (
            <Button
              variant="ghost"
              size="icon"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
              aria-label="Gravar áudio"
            >
              <Mic className="h-5 w-5 text-muted-foreground" />
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default MessageInput;
